import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MDEditor from '@uiw/react-md-editor';
import {
  POST_CATEGORIES,
  opportunitiesService,
  type CreatePostBody,
  type PostCategory,
} from '../../../shared/services/opportunities.service';

/** A post's writable shape — what both Create and Edit pages serialise on
 *  submit. The three image URLs flow through together (all set or all null). */
export interface PostFormValues {
  title: string;
  body: string;
  category: PostCategory;
  imageThumbUrl: string | null;
  imageMediumUrl: string | null;
  imageLargeUrl: string | null;
}

interface Props {
  /** Initial field values. Pass current post on edit, sensible defaults on create. */
  initial?: Partial<PostFormValues>;
  /** Called once the form is valid and the user hits submit. */
  onSubmit: (body: CreatePostBody) => Promise<void> | void;
  /** Optional cancel handler — shown as a "Cancel" button next to Submit. */
  onCancel?: () => void;
  /** True while the parent mutation is in-flight (disables fields + submit). */
  submitting?: boolean;
  /** Label on the submit button — defaults to the i18n "publish" key. */
  submitLabel: string;
  /** Label while submit is in-flight (the i18n "publishing" / "saving" key). */
  submittingLabel: string;
  /** Pre-formatted error from the parent (translated by useApiError). Null to hide. */
  errorMessage?: string | null;
}

const TITLE_MAX = 180;
const BODY_MAX = 4000;
const IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/**
 * Shared create/edit form for opportunity posts. Owns its own local field
 * state + the image upload, then hands a clean {@link CreatePostBody} to the
 * parent on submit. Image upload happens on file selection so the parent
 * doesn't have to juggle MultipartFile + JSON serialisation.
 */
export function PostForm({
  initial,
  onSubmit,
  onCancel,
  submitting,
  submitLabel,
  submittingLabel,
  errorMessage,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [title, setTitle] = useState(initial?.title ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [category, setCategory] = useState<PostCategory>(
    initial?.category ?? 'BUSINESS',
  );
  const [thumbUrl, setThumbUrl] = useState<string | null>(initial?.imageThumbUrl ?? null);
  const [mediumUrl, setMediumUrl] = useState<string | null>(initial?.imageMediumUrl ?? null);
  const [largeUrl, setLargeUrl] = useState<string | null>(initial?.imageLargeUrl ?? null);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const titleValid = title.trim().length > 0 && title.length <= TITLE_MAX;
  const bodyValid = body.trim().length > 0 && body.length <= BODY_MAX;
  const canSubmit = titleValid && bodyValid && !submitting && uploadPercent === null;

  const pickImage = () => fileInputRef.current?.click();

  const clearImage = () => {
    setThumbUrl(null);
    setMediumUrl(null);
    setLargeUrl(null);
    setUploadError(null);
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so the same file can be picked again after a clear
    if (!file) return;
    if (file.size > IMAGE_MAX_BYTES) {
      setUploadError(t('opportunities.form.imageTooLarge'));
      return;
    }
    if (!IMAGE_TYPES.includes(file.type)) {
      setUploadError(t('opportunities.form.imageTypeUnsupported'));
      return;
    }
    setUploadError(null);
    setUploadPercent(0);
    try {
      const out = await opportunitiesService.uploadImage(file, (p) => setUploadPercent(p));
      setThumbUrl(out.thumbUrl);
      setMediumUrl(out.mediumUrl);
      setLargeUrl(out.largeUrl);
    } catch {
      setUploadError(t('opportunities.form.imageTypeUnsupported'));
    } finally {
      setUploadPercent(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    await onSubmit({
      title: title.trim(),
      body: body.trim(),
      category,
      imageThumbUrl: thumbUrl,
      imageMediumUrl: mediumUrl,
      imageLargeUrl: largeUrl,
    });
  };

  const showTitleError = touched && !titleValid;
  const showBodyError = touched && !bodyValid;

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={2.5}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        <TextField
          label={t('opportunities.form.titleLabel')}
          placeholder={t('opportunities.form.titlePlaceholder')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          inputProps={{ maxLength: TITLE_MAX }}
          error={showTitleError}
          helperText={`${title.length} / ${TITLE_MAX}`}
          disabled={submitting}
          fullWidth
          required
        />

        <TextField
          label={t('opportunities.form.categoryLabel')}
          value={category}
          onChange={(e) => setCategory(e.target.value as PostCategory)}
          select
          disabled={submitting}
          fullWidth
          required
        >
          {POST_CATEGORIES.map((c) => (
            <MenuItem key={c} value={c}>
              {t(`opportunities.category.${c}`)}
            </MenuItem>
          ))}
        </TextField>

        {/* Body — full markdown editor with toolbar + live preview tab. The
            MDEditor ships its own textarea; we wrap it in a "field shell"
            that mimics the look of MUI's outlined TextField so the form
            reads as one consistent control. */}
        <Box>
          <Typography
            component="label"
            htmlFor="opportunities-post-body"
            variant="body2"
            sx={{
              display: 'block',
              mb: 0.75,
              fontWeight: 600,
              color: showBodyError ? 'error.main' : 'text.primary',
            }}
          >
            {t('opportunities.form.bodyLabel')} *
          </Typography>
          <Box
            // `data-color-mode` switches the MDEditor between its built-in
            // light / dark stylesheets — match whatever MUI is currently
            // showing so the editor doesn't clash with the rest of the form.
            data-color-mode={theme.palette.mode}
            sx={{
              // Outline the editor like an MUI TextField so it blends in.
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: showBodyError ? 'error.main' : 'divider',
              overflow: 'hidden',
              '&:focus-within': {
                borderColor: showBodyError ? 'error.main' : 'primary.main',
                boxShadow: showBodyError
                  ? `0 0 0 1px ${theme.palette.error.main}`
                  : `0 0 0 1px ${theme.palette.primary.main}`,
              },
              // The editor's own surface should follow the MUI background.
              '& .w-md-editor': {
                bgcolor: 'background.paper',
                color: 'text.primary',
                boxShadow: 'none',
              },
              // Disabled state — fade like an MUI disabled field.
              opacity: submitting ? 0.6 : 1,
              pointerEvents: submitting ? 'none' : 'auto',
            }}
          >
            <MDEditor
              value={body}
              onChange={(next) => {
                // MDEditor doesn't enforce maxLength natively — truncate the
                // value on the way in so the counter never lies and the
                // server never sees > BODY_MAX characters.
                const v = next ?? '';
                setBody(v.length > BODY_MAX ? v.slice(0, BODY_MAX) : v);
              }}
              height={320}
              preview="edit"
              visibleDragbar={false}
              textareaProps={{
                id: 'opportunities-post-body',
                'aria-label': t('opportunities.form.bodyLabel'),
                placeholder: t('opportunities.form.bodyPlaceholder'),
                maxLength: BODY_MAX,
                disabled: submitting,
              }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.75,
              color: showBodyError ? 'error.main' : 'text.secondary',
            }}
          >
            {body.length} / {BODY_MAX} — {t('opportunities.form.bodyHelp')}
          </Typography>
        </Box>

        {/* Image picker — single file, optional. */}
        <Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            {t('opportunities.form.imageLabel')}
          </Typography>

          {mediumUrl ? (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                aspectRatio: '16 / 9',
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: 'action.hover',
                mb: 1,
              }}
            >
              <Box
                component="img"
                src={mediumUrl}
                alt=""
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <IconButton
                onClick={clearImage}
                disabled={submitting}
                aria-label={t('opportunities.form.imageRemove')}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(0,0,0,0.55)',
                  color: 'common.white',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
                size="small"
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : null}

          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Button
              type="button"
              variant="outlined"
              startIcon={<PhotoCameraOutlinedIcon />}
              onClick={pickImage}
              disabled={submitting || uploadPercent !== null}
            >
              {mediumUrl
                ? t('opportunities.form.imageReplace')
                : t('opportunities.form.imagePick')}
            </Button>
            <Typography variant="caption" color="text.secondary">
              {t('opportunities.form.imageHint')}
            </Typography>
          </Stack>

          {uploadPercent !== null && (
            <Box sx={{ mt: 1.5 }}>
              <LinearProgress variant="determinate" value={uploadPercent} />
              <Typography variant="caption" color="text.secondary">
                {t('opportunities.form.uploading')} ({uploadPercent}%)
              </Typography>
            </Box>
          )}
          {uploadError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {uploadError}
            </Alert>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept={IMAGE_TYPES.join(',')}
            onChange={handleFile}
            hidden
          />
        </Box>

        <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ pt: 1 }}>
          {onCancel && (
            <Button type="button" onClick={onCancel} disabled={submitting}>
              {t('opportunities.form.cancel')}
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={!canSubmit}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitting ? submittingLabel : submitLabel}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
